import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Download, Eye, FileText } from 'lucide-react';
import { depositPlatformFeePaise, depositFeeGstPaise } from '../../lib/fees';

const AgreementPDFPreview = forwardRef(({
  formData,
  agreementId,
  agreementType,
  propertyType,
  previewOnly = false,
  userRole = 'tenant', // 'tenant', 'landlord', 'broker', 'agent'
  depositProtectionSelected = false,
  depositAmount = 0,
  feePayer = 'tenant' // 'tenant', 'landlord', 'split'
}, ref) => {
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' | 'upload'
  const [signatureImage, setSignatureImage] = useState(null);
  // Live-captured photo, taken at signing time and stamped into the photo box next
  // to the signature box (see SigningFlowWizard). Only ever set during a live signing
  // session - never populated at step 8 (agreement creation).
  const [photoImage, setPhotoImage] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState('agreement'); // 'agreement' | 'signed'
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const agreementPreviewRef = useRef(null);
  // Signature box positions (page + mm coords) measured when the PDF is generated.
  // The backend uses these to stamp each party's signature into the correct box.
  const signatureBoxesRef = useRef([]);

  // Calculate stamp duty dynamically
  const calculateStampDuty = () => {
    const isCommercial = propertyType === 'commercial';
    const state = formData.property_state || 'Maharashtra';
    const monthlyRent = parseFloat(formData.monthly_rent) || 0;
    const leaseDuration = parseInt(formData.lease_duration_years) || 1;
    const annualRent = monthlyRent * 12;
    const totalRent = annualRent * leaseDuration;

    if (isCommercial) {
      // Commercial rates
      const commercialRates = {
        'Karnataka': 0.1,
        'Kerala': 0.2,
        'Sikkim': 0.2,
        'Tamil Nadu': 1.0,
      };

      if (state === 'Delhi') {
        if (leaseDuration <= 4) {
          return Math.round((annualRent * 0.02) + 100);
        } else if (leaseDuration <= 10) {
          return Math.round((annualRent * 0.05) + 100);
        } else {
          return Math.round((annualRent * 2 * 0.05) + 100);
        }
      } else if (state === 'Uttar Pradesh') {
        return Math.round(totalRent * 0.005);
      } else if (commercialRates[state]) {
        return Math.round(totalRent * (commercialRates[state] / 100));
      } else {
        return Math.round(totalRent * 0.005); // Default 0.5%
      }
    } else {
      // Residential rates
      const residentialRates = {
        'Maharashtra': 0.5,
        'Delhi': 'duration-based',
        'Karnataka': 0.1,
        'Tamil Nadu': 1.0,
        'Uttar Pradesh': 'variable',
        'Rajasthan': 0.25,
        'Gujarat': 0.25,
        'Telangana': 0.25,
        'Andhra Pradesh': 0.3,
        'Haryana': 0.25,
        'Punjab': 0.25,
        'Himachal Pradesh': 0.25,
        'Uttarakhand': 0.25,
        'Goa': 0.25,
        'Assam': 0.25,
        'West Bengal': 0.25,
        'Odisha': 0.25,
        'Jharkhand': 0.25,
        'Chhattisgarh': 0.25,
        'Madhya Pradesh': 0.25,
        'Bihar': 0.25,
        'Kerala': 0.1,
        'Manipur': 0.25,
        'Meghalaya': 0.25,
        'Mizoram': 0.25,
        'Nagaland': 0.25,
        'Sikkim': 0.1,
        'Tripura': 0.25,
      };

      const rate = residentialRates[state];
      if (rate === 'duration-based') {
        return Math.round(totalRent * 0.0025);
      } else if (rate === 'variable') {
        return Math.round(totalRent * 0.0025);
      } else if (rate) {
        return Math.round(totalRent * (rate / 100));
      } else {
        return Math.round(totalRent * 0.0025);
      }
    }
  };

  const stampDuty = calculateStampDuty();
  const registrationFee = 1100;

  // Calculate payment breakdown based on user role
  const calculatePaymentBreakdown = () => {
    const agreementFeeMap = {
      'e_agreement': 9900, // ₹99 in paise
      'e_stamp': 29900 // ₹299 in paise
    };

    const agreementFee = agreementFeeMap[agreementType] || 0;

    // v1.0 marginal slices, ex-GST. Preview only — once the agreement exists the
    // stored price from fee-status is authoritative. See lib/fees.js.
    const depositProtectionFee = depositProtectionSelected
      ? depositPlatformFeePaise(depositAmount * 100)
      : 0;
    const depositProtectionGst = depositFeeGstPaise(depositProtectionFee);

    const brokerCommissionFeeMap = {
      'e_agreement': 2500, // ₹25 flat
      'e_stamp': 5000 // ₹50 flat
    };

    const brokerCommission = brokerCommissionFeeMap[agreementType] || 0;
    // 15% of the fee EXCLUDING GST — GST is a pass-through, not commissionable.
    const depositProtectionCommission = Math.floor(depositProtectionFee * 0.15);

    return {
      agreementFee,
      depositProtectionFee,
      depositProtectionGst,
      depositAmount: depositAmount * 100, // Convert to paise
      brokerCommission,
      depositProtectionCommission,
      feePayer
    };
  };

  const paymentBreakdown = calculatePaymentBreakdown();

  // Payment breakdown for display
  const getPaymentDisplayItems = () => {
    const items = [];

    // Determine user's payment obligations
    if (userRole === 'tenant') {
      // Tenant pays: agreement fee (if applicable) + deposit protection fee (if applicable) + deposit amount
      const tenantAgreementFee = feePayer === 'tenant' || feePayer === 'split' ? paymentBreakdown.agreementFee : 0;

      if (tenantAgreementFee > 0) {
        items.push({
          label: agreementType === 'e_agreement' ? 'E-Agreement Fee' : 'E-Stamp Fee',
          amount: tenantAgreementFee,
          account: 'Orello Current Account',
          description: agreementType === 'e_agreement' ? 'Digital agreement processing' : 'Digital stamp registration'
        });
      }

      if (depositProtectionSelected && paymentBreakdown.depositProtectionFee > 0) {
        items.push({
          label: 'Deposit Platform Fee',
          amount: paymentBreakdown.depositProtectionFee,
          account: 'Orello Current Account',
          description: 'Tenant protection service (excl. GST)'
        });
        items.push({
          label: 'GST (18%)',
          amount: paymentBreakdown.depositProtectionGst,
          account: 'Orello Current Account',
          description: 'Tax on the deposit platform fee'
        });
      }

      if (paymentBreakdown.depositAmount > 0) {
        items.push({
          label: 'Security Deposit',
          amount: paymentBreakdown.depositAmount,
          account: 'Orello Escrow Account (Secure)',
          description: 'Held for tenant protection until lease end'
        });
      }
    } else if (userRole === 'landlord') {
      // Landlord pays: only their portion of agreement fee (if applicable)
      const landlordAgreementFee = feePayer === 'landlord' || feePayer === 'split' ? paymentBreakdown.agreementFee : 0;

      if (landlordAgreementFee > 0) {
        items.push({
          label: agreementType === 'e_agreement' ? 'E-Agreement Fee' : 'E-Stamp Fee',
          amount: landlordAgreementFee,
          account: 'Orello Current Account',
          description: agreementType === 'e_agreement' ? 'Digital agreement processing' : 'Digital stamp registration'
        });
      }

      if (items.length === 0) {
        items.push({
          label: 'No Payment Required',
          amount: 0,
          account: 'N/A',
          description: 'Tenant will handle all agreement costs'
        });
      }
    } else if (userRole === 'broker' || userRole === 'agent') {
      // Broker/Agent do not pay anything, they earn commission
      items.push({
        label: 'Commission Earned (E-Agreement)',
        amount: paymentBreakdown.brokerCommission,
        account: 'Your Orello Wallet',
        description: 'Will be credited upon agreement completion',
        isEarning: true
      });

      if (depositProtectionSelected && paymentBreakdown.depositProtectionCommission > 0) {
        items.push({
          label: 'Deposit Protection Commission',
          amount: paymentBreakdown.depositProtectionCommission,
          account: 'Your Orello Wallet',
          description: '15% of protection fee',
          isEarning: true
        });
      }
    }

    return items;
  };

  // Initialize canvas for signature drawing
  useEffect(() => {
    if (signatureMode === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas size
      canvas.width = 400;
      canvas.height = 200;

      // Set white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Set border
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, canvas.width, canvas.height);

      // Setup drawing
      let isDrawing = false;

      const startDrawing = (e) => {
        isDrawing = true;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        ctx.beginPath();
        ctx.moveTo(x, y);
      };

      const draw = (e) => {
        if (!isDrawing) return;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        ctx.lineTo(x, y);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
      };

      const stopDrawing = () => {
        isDrawing = false;
      };

      canvas.addEventListener('mousedown', startDrawing);
      canvas.addEventListener('mousemove', draw);
      canvas.addEventListener('mouseup', stopDrawing);
      canvas.addEventListener('mouseleave', stopDrawing);

      return () => {
        canvas.removeEventListener('mousedown', startDrawing);
        canvas.removeEventListener('mousemove', draw);
        canvas.removeEventListener('mouseup', stopDrawing);
        canvas.removeEventListener('mouseleave', stopDrawing);
      };
    }
  }, [signatureMode]);

  // Handle signature canvas capture
  const captureSignature = () => {
    if (canvasRef.current) {
      const imageData = canvasRef.current.toDataURL('image/png');
      setSignatureImage(imageData);
    }
  };

  // Clear signature canvas
  const clearSignature = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setSignatureImage(null);
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSignatureImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Generate Agreement Content
  const generateAgreementContent = ({ includeSignature = false }) => {
    const isCommercial = propertyType === 'commercial';
    const isTenant = !isCommercial;

    return (
      <div style={{
        backgroundColor: '#ffffff',
        color: '#2c3e50',
        padding: '50px',
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        lineHeight: '1.7',
        fontSize: '10px'
      }}>
        {/* Professional Header - Without Orello branding as requested */}
        <div style={{
          marginBottom: '40px',
          paddingBottom: '20px',
          borderBottom: '3px solid #2c3e50',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '10px' }}>
              Property Rental Management Agreement
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: '9px', color: '#7f8c8d', lineHeight: '1.5' }}>
            <div style={{ fontWeight: '600', color: '#2c3e50' }}>Document ID:</div>
            <div>{agreementId || 'AGR-' + new Date().getTime().toString().slice(-8)}</div>
            <div style={{ marginTop: '8px', fontWeight: '600', color: '#2c3e50' }}>Generated:</div>
            <div>{new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
        </div>

        {/* Document Title */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{
            margin: '0 0 8px 0',
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#2c3e50',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            PROPERTY LEASE AGREEMENT
          </h1>
          <div style={{ fontSize: '12px', color: '#F97316', fontWeight: '600', marginBottom: '12px' }}>
            {isCommercial ? '📊 Commercial Property' : '🏠 Residential Property'}
          </div>
          <div style={{
            fontSize: '11px',
            color: '#7f8c8d',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '15px',
            maxWidth: '400px',
            margin: '15px auto'
          }}>
            <div><strong>State:</strong> {formData.property_state || 'N/A'}</div>
            <div><strong>Duration:</strong> {formData.lease_duration_years || 'N/A'} year(s)</div>
            <div><strong>Start Date:</strong> {formData.start_date || formData.lease_start_date || 'N/A'}</div>
            <div><strong>Type:</strong> {isCommercial ? formData.commercial_property_type : formData.property_type}</div>
          </div>
        </div>

        {/* Party Details - Professional Table Layout */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '13px',
            fontWeight: 'bold',
            marginBottom: '15px',
            paddingBottom: '8px',
            borderBottom: '2px solid #F97316',
            color: '#2c3e50',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            1. PARTIES TO THIS AGREEMENT
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Lessor/Licensor */}
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '15px',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#F97316',
                marginBottom: '10px',
                textTransform: 'uppercase'
              }}>
                Property Owner (Lessor)
              </div>
              <div style={{ fontSize: '10px', lineHeight: '1.8', color: '#2c3e50' }}>
                <div><strong>Name:</strong> {isCommercial ? formData.lessor_full_name : formData.licensor_full_name || '___________________'}</div>
                <div><strong>Age:</strong> {isCommercial ? formData.lessor_age : formData.licensor_age || 'N/A'}</div>
                <div><strong>ID/Aadhaar:</strong> {isCommercial ? formData.lessor_pan_aadhaar : formData.licensor_pan_aadhaar || 'N/A'}</div>
                <div><strong>Address:</strong> {isCommercial ? formData.lessor_address : formData.licensor_address || 'N/A'}</div>
                <div><strong>Phone:</strong> {isCommercial ? formData.lessor_phone : formData.licensor_phone || 'N/A'}</div>
                <div><strong>Email:</strong> {isCommercial ? formData.lessor_email : formData.licensor_email || 'N/A'}</div>
              </div>
            </div>

            {/* Lessee/Licensee */}
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '15px',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#F97316',
                marginBottom: '10px',
                textTransform: 'uppercase'
              }}>
                Tenant (Lessee)
              </div>
              <div style={{ fontSize: '10px', lineHeight: '1.8', color: '#2c3e50' }}>
                <div><strong>Name:</strong> {isCommercial ? formData.lessee_full_name : formData.licensee_full_name || '___________________'}</div>
                <div><strong>Age:</strong> {isCommercial ? formData.lessee_age : formData.licensee_age || 'N/A'}</div>
                <div><strong>ID/Aadhaar:</strong> {isCommercial ? formData.lessee_pan_aadhaar : formData.licensee_pan_aadhaar || 'N/A'}</div>
                <div><strong>Address:</strong> {isCommercial ? formData.lessee_address : formData.licensee_address || 'N/A'}</div>
                <div><strong>Phone:</strong> {isCommercial ? formData.lessee_phone : formData.licensee_phone || 'N/A'}</div>
                <div><strong>Email:</strong> {isCommercial ? formData.lessee_email : formData.licensee_email || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Property Details */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px', borderBottom: '1px solid #ccc', paddingBottom: '5px' }}>
            PROPERTY DETAILS
          </h3>
          <p style={{ margin: '5px 0', fontSize: '10px' }}>
            Property Type: {isCommercial ? formData.commercial_property_type : formData.property_type || 'N/A'}<br/>
            Address: {formData.property_address || 'N/A'}<br/>
            City: {formData.property_city || 'N/A'}<br/>
            State: {formData.property_state || 'N/A'}<br/>
            PIN: {formData.property_pin || 'N/A'}<br/>
            {isCommercial && (
              <>
                Built-up Area: {formData.property_built_area || 'N/A'} sq. ft.<br/>
                Carpet Area: {formData.property_carpet_area || 'N/A'} sq. ft.
              </>
            )}
            {!isCommercial && (
              <>
                Area: {formData.property_built_area || 'N/A'} sq. ft.<br/>
                Rooms: {formData.property_rooms || 'N/A'}
              </>
            )}
          </p>
        </div>

        {/* Financial Terms - Comprehensive Breakdown */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '12px',
            fontWeight: 'bold',
            marginBottom: '15px',
            paddingBottom: '8px',
            borderBottom: '2px solid #2c3e50',
            color: '#2c3e50',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            2. FINANCIAL TERMS & CONDITIONS
          </h2>

          {/* Primary Financial Components */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#fffbf7'
            }}>
              <div style={{ fontSize: '9px', color: '#7f8c8d', fontWeight: '600', marginBottom: '5px', textTransform: 'uppercase' }}>Monthly Rent</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' }}>
                ₹{(formData.monthly_rent || 0).toLocaleString()}
              </div>
            </div>
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#f0f8f5'
            }}>
              <div style={{ fontSize: '9px', color: '#7f8c8d', fontWeight: '600', marginBottom: '5px', textTransform: 'uppercase' }}>Security Deposit</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#27ae60' }}>
                ₹{(formData.security_deposit || 0).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Key Financial Terms Table */}
          <div style={{
            border: '1px solid #ecf0f1',
            borderRadius: '6px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            fontSize: '9px',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 25px', lineHeight: '1.8', color: '#2c3e50' }}>
              <div><strong>Lease Duration:</strong> {formData.lease_duration_years || 'N/A'} year(s) {formData.lease_start_date ? `(${(parseInt(formData.lease_duration_years) || 1) * 12} months)` : ''}</div>
              <div><strong>Start Date:</strong> {formData.start_date || formData.lease_start_date || 'N/A'}</div>
              <div><strong>End Date:</strong> {formData.end_date || formData.lease_end_date || 'As per lease duration'}</div>
              <div><strong>Rent Due Date:</strong> {formData.rent_due_date || '1st of each month'}</div>
              <div><strong>Annual Escalation:</strong> {formData.escalation_percent || '0'}% from anniversary date</div>
              <div><strong>Late Payment Fee:</strong> ₹{formData.late_fee || '0'}/day (after 5 days)</div>
            </div>
          </div>

          {/* Detailed Payment Breakdown */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50', marginBottom: '10px', display: 'block' }}>2.1 Monthly Payment Structure & Breakdown</strong>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '9px',
              marginBottom: '10px',
              color: '#000000'
            }}>
              <tbody>
                <tr style={{ backgroundColor: '#ecf0f1' }}>
                  <td style={{ border: '1px solid #bdc3c7', padding: '8px', fontWeight: 'bold', color: '#2c3e50' }}>Payment Component</td>
                  <td style={{ border: '1px solid #bdc3c7', padding: '8px', fontWeight: 'bold', textAlign: 'right', color: '#2c3e50' }}>Amount</td>
                  <td style={{ border: '1px solid #bdc3c7', padding: '8px', fontWeight: 'bold', color: '#2c3e50' }}>Paid By</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', color: '#2c3e50' }}>Base Monthly Rent</td>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', textAlign: 'right', color: '#2c3e50' }}>₹{(formData.monthly_rent || 0).toLocaleString()}</td>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', color: '#2c3e50' }}>{formData.rent_payer || 'Lessee'}</td>
                </tr>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', color: '#2c3e50' }}>Maintenance/Upkeep Charges</td>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', textAlign: 'right', color: '#2c3e50' }}>₹{formData.maintenance_charges || '0'}</td>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', color: '#2c3e50' }}>{formData.maintenance_payer || 'As agreed'}</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', color: '#2c3e50' }}>Water Charges</td>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', textAlign: 'right', color: '#2c3e50' }}>₹{formData.water_charges || 'As metered'}</td>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', color: '#2c3e50' }}>{formData.water_payer || 'Lessee'}</td>
                </tr>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', color: '#2c3e50' }}>Electricity Charges</td>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', textAlign: 'right', color: '#2c3e50' }}>₹{formData.electricity_charges || 'As metered'}</td>
                  <td style={{ border: '1px solid #ecf0f1', padding: '8px', color: '#2c3e50' }}>{formData.electricity_payer || 'Lessee'}</td>
                </tr>
                <tr style={{ backgroundColor: '#ecf0f1', fontWeight: 'bold' }}>
                  <td style={{ border: '1px solid #bdc3c7', padding: '8px', color: '#2c3e50' }}>Total Monthly Liability</td>
                  <td style={{ border: '1px solid #bdc3c7', padding: '8px', textAlign: 'right', color: '#2c3e50' }}>₹{(
                    (parseFloat(formData.monthly_rent) || 0) +
                    (parseFloat(formData.maintenance_charges) || 0)
                  ).toLocaleString()}</td>
                  <td style={{ border: '1px solid #bdc3c7', padding: '8px', color: '#2c3e50' }}>Various</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Security Deposit Details */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50', marginBottom: '10px', display: 'block' }}>2.2 Security Deposit Details</strong>
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#f0f8f5',
              fontSize: '9px',
              lineHeight: '1.7'
            }}>
              <div><strong>Security Deposit Amount:</strong> ₹{(formData.security_deposit || 0).toLocaleString()}</div>
              <div><strong>Deposit Held By:</strong> {depositProtectionSelected ? 'Orello' : 'The Lessor'}</div>
              <div><strong>Deposit Equivalent to Months of Rent:</strong> {((formData.security_deposit || 0) / (formData.monthly_rent || 1)).toFixed(1)} months</div>
              <div><strong>Purpose:</strong> Security against unpaid rent and property damages</div>
              <div><strong>Refund Timeline:</strong> Upon lease termination or contract end, deductions may apply</div>
              <div><strong>Rent Arrears Clause:</strong> Any unpaid rent dues will be deducted from the deposit and given to the Landlord</div>
              <div><strong>Interest (if not refunded within 60 days):</strong> 4% per annum</div>
              <div><strong>Deductible Items:</strong> Unpaid rent, utilities, damages, stains, breakage beyond normal wear & tear</div>
            </div>
          </div>

          {/* Annual Rent Escalation */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50', marginBottom: '10px', display: 'block' }}>2.3 Annual Rent Escalation</strong>
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#fffbf7',
              fontSize: '9px',
              lineHeight: '1.8'
            }}>
              <div><strong>Escalation Rate:</strong> {formData.escalation_percent || '0'}% per annum</div>
              <div><strong>Escalation Trigger:</strong> Every 12 months from the lease commencement date</div>
              <div><strong>Example (if applicable):</strong> Year 1: ₹{(formData.monthly_rent || 0).toLocaleString()} | Year 2: ₹{Math.round((formData.monthly_rent || 0) * (1 + (parseInt(formData.escalation_percent) || 0) / 100)).toLocaleString()}</div>
              <div><strong>Maximum Escalation Cap:</strong> Not to exceed {(parseInt(formData.escalation_percent) || 5)}% of previous year's rent</div>
            </div>
          </div>

          {/* Late Payment & Penalty Terms */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50', marginBottom: '10px', display: 'block' }}>2.4 Late Payment & Penalties</strong>
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#fff5f5',
              fontSize: '9px',
              lineHeight: '1.8'
            }}>
              <div><strong>Grace Period:</strong> 5 days after due date without penalty</div>
              <div><strong>Daily Late Fee:</strong> ₹{formData.late_fee || '0'} per day after grace period</div>
              <div><strong>Cheque Bounce Penalty:</strong> ₹500 + late fees applicable</div>
              <div><strong>Cumulative Penalty Cap:</strong> Not to exceed 5% of monthly rent per instance</div>
              <div><strong>Payment Method:</strong> Bank transfer, cheque, DD, or digital payment (as agreed)</div>
              <div><strong>Payment Recipient:</strong> {formData.lessor_account || 'As specified by Lessor in writing'}</div>
            </div>
          </div>

        </div>

        {/* Legal Disclaimer */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            border: '2px solid #e74c3c',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: 'rgba(231, 76, 60, 0.08)',
            marginBottom: '20px'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#c0392b',
              marginBottom: '10px',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ⚠️ IMPORTANT DISCLAIMER
            </div>
            <div style={{
              fontSize: '10px',
              color: '#c0392b',
              lineHeight: '1.7',
              margin: 0
            }}>
              <strong>This is an informal/informal agreement document.</strong> This agreement is <strong>NOT legally valid in court</strong> and does not have the status of a registered agreement. It is intended for mutual understanding between parties only.
              <br/><br/>
              <strong>For legal enforceability:</strong> If you require a court-valid agreement with legal protection, stamp duty, and government registration, please opt for the <strong>E-Stamp Agreement</strong> service which includes government stamp registration and legal validity.
            </div>
          </div>
        </div>

        {/* Terms & Conditions - Comprehensive Professional */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '12px',
            fontWeight: 'bold',
            marginBottom: '15px',
            paddingBottom: '8px',
            borderBottom: '2px solid #2c3e50',
            color: '#2c3e50',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            3. TERMS & CONDITIONS
          </h2>

          {/* 3.1 Payment Terms */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.1 Rent Payment Terms</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>The Lessee shall pay monthly rent of ₹{(formData.monthly_rent || 0).toLocaleString()} on the {formData.rent_due_date || '1st'} of each month.</li>
              <li>Rent payment shall be made to the Lessor's account as specified in writing or through the designated portal.</li>
              <li>Payment must be received in full by 5:00 PM on the due date to avoid late fees.</li>
              <li>Late payment of rent shall incur a penalty of ₹{formData.late_fee || '0'} per day after 5 days of non-payment.</li>
              <li>Rent shall increase by {formData.escalation_percent || '0'}% annually on the anniversary date of this agreement.</li>
              <li>Cheques/DD returned due to insufficient funds shall attract an additional penalty of ₹500.</li>
            </ol>
          </div>

          {/* 3.2 Security Deposit */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.2 Security Deposit & Refund</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>Security deposit of ₹{(formData.security_deposit || 0).toLocaleString()} is held by {depositProtectionSelected ? 'Orello' : 'the Lessor'} as security against default or damage.</li>
              <li>The security deposit shall be returned upon the end of the tenancy term or when the contract is terminated, deductions may apply.</li>
              <li>Deductions from the security deposit may be made for: unpaid rent, damage beyond normal wear, outstanding utilities, or breach of agreement terms.</li>
              <li>If rent remains unpaid at the time of lease termination, the sum of rent due shall be deducted from the security deposit and given to the Landlord.</li>
              <li>An itemized list of deductions, if any, shall be provided to the Lessee within 15 days of lease termination.</li>
              <li>The party holding the security deposit shall pay interest at 4% per annum on the security deposit if held beyond 60 days after lease termination without refund.</li>
              <li>In case of disputes regarding security deposit deductions, the matter shall be referred to mediation.</li>
            </ol>
          </div>

          {/* 3.3 Use of Property */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.3 Permitted Use & Restrictions</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>The property shall be used exclusively for {formData.intended_use || 'residential purposes'}.</li>
              <li>The Lessee shall not use the property for any illegal, immoral, or hazardous activities.</li>
              <li>The Lessee shall not operate any commercial business, manufacturing unit, or create public nuisance from the property.</li>
              <li>The Lessee shall not sublet, lease, or allow any unauthorized occupant to reside in the property without prior written consent from the Lessor.</li>
              <li>The maximum number of occupants shall not exceed {formData.max_occupants || 'as mutually agreed'} persons.</li>
              <li>The Lessee shall not keep explosive, flammable, or hazardous materials in the property.</li>
              <li>The Lessee shall not keep any pet or animal without prior written approval from the Lessor.</li>
              <li>The Lessee shall comply with all building regulations, bye-laws, and housing society norms.</li>
            </ol>
          </div>

          {/* 3.4 Maintenance Responsibilities */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.4 Maintenance & Repair Responsibilities</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li><strong>Lessor's Responsibility:</strong> The Lessor shall maintain structural integrity, roof, walls, plumbing, electrical wiring, and major installations.</li>
              <li><strong>Lessor's Maintenance:</strong> The Lessor shall repair or replace structural defects, non-functional fixtures, and ensure water supply and electricity access.</li>
              <li><strong>Lessee's Responsibility:</strong> The Lessee shall maintain internal cleanliness, paint, minor repairs, and day-to-day upkeep.</li>
              <li><strong>Lessee's Maintenance:</strong> The Lessee shall replace broken window panes, light bulbs, taps, switches, and repair internal damages caused by Lessee.</li>
              <li>The Lessee shall report all maintenance issues to the Lessor in writing within 24 hours of discovery.</li>
              <li>The Lessor shall respond to urgent repairs within 48 hours and non-urgent repairs within 7 days.</li>
              <li>Emergency repairs (water leakage, electrical failure, etc.) shall be undertaken immediately regardless of cost approval.</li>
              <li>Normal wear and tear shall not be the Lessee's liability; however, negligent damage shall be charged to the Lessee.</li>
            </ol>
          </div>

          {/* 3.5 Utilities & Services */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.5 Utilities, Services & Charges</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>Electricity shall be metered separately; the Lessee shall pay as per consumption.</li>
              <li>Water charges shall be paid by {formData.water_payer || 'Lessee'} as per {formData.water_billing || 'meter consumption'}}.</li>
              <li>Gas charges (if applicable) shall be paid by the Lessee at actual cost.</li>
              <li>Maintenance charges/society fees of ₹{formData.maintenance_charges || '0'} shall be paid by {formData.maintenance_payer || 'Lessee'}.</li>
              <li>Property tax and municipal taxes shall be paid by the {formData.property_tax_payer || 'Lessor'}.</li>
              <li>Internet/Cable charges, if any, shall be arranged and paid by the Lessee.</li>
              <li>The Lessee shall not make any structural modifications to utility connections.</li>
            </ol>
          </div>

          {/* 3.6 Damage Assessment */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.6 Damage Assessment & Liability</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>The Lessee shall be liable for all damages caused by negligence, misuse, or unauthorized modifications.</li>
              <li>A joint inspection shall be conducted on lease commencement and termination to document property condition.</li>
              <li>The Lessee shall be responsible for damage to furniture, fixtures, flooring, and interior elements during occupancy.</li>
              <li>Accidental damage not caused by gross negligence may be covered under mutual agreement between parties.</li>
              <li>The cost of repairs shall be borne by the liable party as determined by the damage assessment report.</li>
              <li>The Lessee shall maintain insurance for personal belongings; the Lessor is not liable for theft or loss.</li>
            </ol>
          </div>

          {/* 3.7 Property Entry & Inspection */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.7 Property Entry Rights & Inspections</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>The Lessor (or authorized representative) may enter the property with 24 hours' written notice for repairs, inspection, or showing to prospective tenants.</li>
              <li>Emergency entry (fire, flood, gas leak) requires immediate access without prior notice for safety purposes.</li>
              <li>Inspections shall be conducted during business hours (9 AM to 5 PM) on weekdays, unless mutually agreed.</li>
              <li>The Lessee has the right to be present during inspections and repairs.</li>
              <li>The Lessee shall provide access to all rooms and attached areas; unreasonable denial may constitute breach of agreement.</li>
              <li>The Lessor shall not abuse entry rights or unnecessarily intrude on Lessee's privacy.</li>
            </ol>
          </div>

          {/* 3.8 Subletting & Assignment */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.8 Subletting & Assignment Restrictions</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>The Lessee shall NOT sublet, lease, or assign the property or any part thereof without prior written consent from the Lessor.</li>
              <li>Subletting without consent shall constitute a material breach, leading to immediate termination and eviction.</li>
              <li>If subletting is permitted, the Lessee remains liable for all obligations under this agreement.</li>
              <li>The Lessor may charge a nominal processing fee for subletting approval.</li>
              <li>Rent during subletting period shall remain unchanged; the Lessee cannot increase rent for sub-tenants.</li>
              <li>The sub-tenant shall comply with all terms of this agreement as if a direct lessee.</li>
            </ol>
          </div>

          {/* 3.9 Insurance & Liability */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.9 Insurance & Liability Coverage</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>The Lessor shall maintain property insurance covering structural damage.</li>
              <li>The Lessee shall obtain renters' insurance for personal belongings and liability coverage.</li>
              <li>The Lessor shall not be liable for personal injury, theft, fire, or natural disasters unless caused by Lessor's negligence.</li>
              <li>The Lessee shall indemnify the Lessor against claims arising from Lessee's occupancy or activities.</li>
              <li>Both parties shall ensure compliance with fire safety norms and emergency procedures.</li>
            </ol>
          </div>

          {/* 3.10 Default & Breach */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.10 Default & Remedies</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>Non-payment of rent for 5 consecutive days shall be considered a default.</li>
              <li>Repeated defaults (more than 2 instances in 12 months) shall be grounds for termination.</li>
              <li>In case of default exceeding 30 days, the Lessor may issue a 60-day termination notice.</li>
              <li>In case of default exceeding 60 days, the Lessor may proceed with eviction without further notice.</li>
              <li>The Lessee shall be liable for all legal costs, eviction charges, and outstanding rent.</li>
              <li>Any material breach of this agreement (unauthorized occupants, illegal use, subletting without consent) shall be grounds for immediate termination.</li>
              <li>Upon default, the Lessor may recover outstanding rent and damages through legal proceedings.</li>
            </ol>
          </div>

          {/* 3.11 Termination & Exit */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.11 Lease Termination & Notice Period</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>Either party may terminate this agreement by providing {formData.lock_in_months || '2'} months' written notice to the other party.</li>
              <li>Notice of termination must be given in writing via email, registered letter, or personal delivery with acknowledgment.</li>
              <li>The Lessee must vacate the property on or before the notice period expiration date.</li>
              <li>The Lessor shall not retain the property beyond the termination date; failure may lead to loss of deposit/penalties.</li>
              <li>Upon termination, the Lessee shall return all keys, access cards, and property documents.</li>
              <li>The final walk-through inspection shall be conducted within 7 days of vacation to assess damages.</li>
              <li>Early termination (before notice period) shall result in forfeiture of one month's rent or security deposit (whichever is higher).</li>
            </ol>
          </div>

          {/* 3.12 Lock-in Period */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.12 Lock-in Period</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>The Lessee shall not terminate the lease during the first {formData.lock_in_months || '2'} months without losing the security deposit.</li>
              <li>Termination during the lock-in period shall result in forfeiture of the full security deposit and one month's rent.</li>
              <li>Exception: Job transfer with employer letter or health emergency may be considered for early exit without penalty.</li>
            </ol>
          </div>

          {/* 3.13 Renewal & Extension */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.13 Renewal & Extension Terms</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>If both parties agree to renewal, a fresh agreement shall be executed {formData.renewal_notice || '30'} days before lease expiration.</li>
              <li>Rent during renewal may be revised by mutual consent; the proposed rent shall be communicated 60 days prior to lease end.</li>
              <li>All terms remain the same unless specifically amended in the renewal agreement.</li>
              <li>Continued occupancy beyond the lease term without a renewal agreement shall be on a month-to-month basis at the original rent.</li>
              <li>Either party may terminate the month-to-month tenancy with 30 days' written notice.</li>
            </ol>
          </div>

          {/* 3.14 State-Specific Compliance (Maharashtra) */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.14 Maharashtra Rent Control Act & Legal Compliance</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>This agreement is governed by the Maharashtra Rent Control Act, 1999, and applicable housing laws.</li>
              <li>The property address registration with local authorities (if applicable) must be maintained.</li>
              <li>Both parties acknowledge that this agreement is intended for mutual understanding and is not registered with authorities unless legally mandated.</li>
              <li>Property tax dues shall be settled by the due date; non-payment may result in legal action against the Lessor.</li>
              <li>The Lessee shall not claim tenancy rights beyond the lease term without legal proceedings.</li>
              <li>For disputes under Maharashtra Rent Control Act, the matter shall be referred to the Rent Controller.</li>
            </ol>
          </div>

          {/* 3.15 Environmental & Municipal Compliance */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.15 Environmental & Municipal Regulations</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>The Lessee shall comply with local municipal bye-laws and waste management regulations.</li>
              <li>The Lessee shall segregate waste (wet, dry, hazardous) as per municipal guidelines.</li>
              <li>The Lessee shall not create noise pollution or air pollution beyond permissible limits.</li>
              <li>The Lessee shall not discharge hazardous materials or untreated wastewater.</li>
              <li>The Lessee shall participate in community cleanliness drives and society maintenance activities.</li>
              <li>The Lessor shall ensure the property complies with building safety and environmental standards.</li>
            </ol>
          </div>

          {/* 3.16 Force Majeure */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.16 Force Majeure Events</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>Force majeure events include war, natural disaster, pandemic, earthquake, flood, or government action beyond parties' control.</li>
              <li>During force majeure events affecting habitation (structural damage, utility disruption), rent shall be proportionally reduced or suspended.</li>
              <li>The Lessee may temporarily vacate during force majeure without penalty; rent shall be adjusted accordingly.</li>
              <li>If the property becomes uninhabitable for more than 30 consecutive days, the Lessee may terminate without penalty.</li>
              <li>The Lessor shall notify the Lessee immediately of any force majeure impacts on the property.</li>
            </ol>
          </div>

          {/* 3.17 Notices & Communication */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.17 Notices & Communication Protocol</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>All formal notices shall be in writing and delivered via email, registered letter, or personal delivery with signature.</li>
              <li>The notice period shall commence from the date of delivery (or next day for registered letters).</li>
              <li>Notices via email shall be considered valid if acknowledged by the recipient.</li>
              <li>Emergency notices (repairs, safety issues) may be communicated via SMS/call; written confirmation must follow within 24 hours.</li>
              <li>Both parties shall maintain updated contact information and notify changes within 7 days.</li>
            </ol>
          </div>

          {/* 3.18 Dispute Resolution & Mediation through Orello */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.18 Dispute Resolution & Mediation through Orello</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li><strong>Agreement to Orello Mediation:</strong> Both the Lessor and Lessee explicitly agree that any dispute, disagreement, or claim arising from this lease agreement shall be resolved through Orello's dispute resolution mechanism. Both parties voluntarily and knowingly accept Orello as the final mediator and arbiter for all disputes.</li>
              <li><strong>Basis of Orello's Decisions:</strong> All decisions made by Orello shall be based on the check-in and check-out inspection reports, photographic/video evidence, documented communications, payment records, and any other objective proof provided by either party. Orello shall make decisions in good faith based on available documentary evidence.</li>
              <li><strong>Binding Nature of Orello's Decision:</strong> Both parties agree that Orello's decision on any dispute shall be FINAL and BINDING. Neither party may appeal, challenge, or seek reversal of Orello's decision through legal proceedings or any other mechanism. Both parties accept Orello's decision as conclusive resolution of the matter.</li>
              <li><strong>Liability Waiver - Property Authority:</strong> Both parties acknowledge that Orello does not own the property and has no authority over the property's legal status, registration, or municipal compliance. Neither party shall hold Orello responsible for any disputes related to the property's ownership, legality, title, encumbrances, or any third-party claims against the property. The Lessor guarantees that the property is legally theirs to lease.</li>
              <li><strong>Liability Waiver - Character & Conduct:</strong> Both parties acknowledge that Orello cannot and does not verify the character, background, financial status, or conduct of either party. Neither party shall hold Orello responsible for any losses, theft, damage, or issues caused by the other party's character, conduct, morality, or integrity. Each party is responsible for their own due diligence before entering this agreement.</li>
              <li><strong>Liability Waiver - Third-Party Issues:</strong> Both parties agree that Orello shall not be held responsible for disputes arising from third-party claims, including claims by co-tenants, neighbors, authorities, creditors, family members, or any person not a signatory to this agreement. Orello's role is limited to disputes between the Lessor and Lessee only.</li>
              <li><strong>No Responsibility for Decision Outcomes:</strong> Both parties explicitly acknowledge and accept that Orello's decision may not be in their favor. Both parties waive their right to hold Orello responsible, liable, or accountable for any disagreement with Orello's decision, including claims that the decision is unfair, biased, incorrect, or not in their favor. The parties accept the outcome regardless of personal satisfaction.</li>
              <li><strong>Scope of Orello's Authority:</strong> Orello's authority is limited to reviewing available evidence and making decisions based on the lease terms and check-in/check-out reports. Orello does NOT have authority to: (a) change lease terms or conditions, (b) forgive rent or other dues, (c) override mutual agreements between parties, (d) enforce external laws beyond lease terms, (e) settle matters requiring legal validation, or (f) adjudicate criminal or civil matters requiring court jurisdiction.</li>
              <li><strong>No Liability for Mediation Process:</strong> Both parties accept that Orello provides mediation services in good faith. Neither party shall hold Orello liable for the quality, fairness, speed, or outcome of the mediation process. Orello shall not be responsible for errors in judgment, procedural issues, or any dissatisfaction with how the mediation was conducted.</li>
              <li><strong>Finality and Non-Reopening:</strong> Once Orello makes a decision, that decision cannot be reopened, reconsidered, or appealed by either party. Both parties shall accept and implement Orello's decision immediately without further delay or dispute. Any grievance post-decision shall not be entertained by Orello or any authority.</li>
              <li><strong>No Authority Over Property Rights:</strong> Both parties acknowledge that Orello's mediation is limited to disputes arising from this lease agreement only. Orello has no authority to determine property rights, ownership issues, legal claims, or matters requiring registration or government involvement. Such matters must be pursued through appropriate legal channels, not through Orello.</li>
              <li><strong>Mutual Indemnification:</strong> Both parties mutually agree to indemnify and hold harmless Orello from any legal action, claim, or demand arising from Orello's dispute resolution decisions, mediation process, or any action or inaction by Orello in its capacity as mediator. This indemnification applies regardless of the outcome or any party's dissatisfaction with the decision.</li>
            </ol>
          </div>

          {/* 3.19 Governing Law & Jurisdiction */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.19 Governing Law & Legal Jurisdiction</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>This agreement shall be governed by and construed in accordance with the laws of India, specifically applicable state laws.</li>
              <li>The exclusive jurisdiction for any legal dispute shall be the {formData.property_state || 'Maharashtra'} courts.</li>
              <li>The Rent Controller shall have concurrent jurisdiction for disputes under the Rent Control Act.</li>
              <li>The Lessee submits to the exclusive jurisdiction of the courts in the district where the property is located.</li>
            </ol>
          </div>

          {/* 3.20 Modification & Amendments */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.20 Modification & Amendments</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>This agreement can only be modified by written consent of both parties.</li>
              <li>Any amendment must be signed by both parties and attached as an addendum to the original agreement.</li>
              <li>Verbal agreements or SMS/chat messages shall not be considered valid modifications.</li>
              <li>The most recent written amendment shall supersede all previous terms on the subject matter.</li>
            </ol>
          </div>

          {/* 3.21 Severability Clause */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.21 Severability & Waiver</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>If any clause of this agreement is deemed invalid or unenforceable, that clause shall be removed, and the remaining agreement shall remain in effect.</li>
              <li>The invalidity of one clause shall not affect the validity of other clauses.</li>
              <li>Waiver of any breach by either party shall not constitute waiver of subsequent breaches.</li>
              <li>Non-enforcement of any clause does not imply waiver of rights under that clause.</li>
            </ol>
          </div>

          {/* 3.22 Entire Agreement */}
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.22 Entire Agreement Clause</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>This agreement, along with all schedules and addenda, constitutes the entire agreement between the parties.</li>
              <li>All prior discussions, negotiations, and preliminary agreements are superseded by this document.</li>
              <li>No party can claim reliance on oral promises or side agreements not reflected in this written document.</li>
              <li>Any future amendments must be in writing and signed by both parties.</li>
            </ol>
          </div>

          {/* 3.23 Special Conditions */}
          <div style={{ marginBottom: '0px' }}>
            <strong style={{ fontSize: '10px', color: '#2c3e50' }}>3.23 Special Conditions & Additional Terms</strong>
            <ol style={{ margin: '5px 0 0 0', fontSize: '9px', paddingLeft: '20px', lineHeight: '1.6', color: '#2c3e50' }}>
              <li>{formData.special_condition_1 || 'No special conditions specified.'}</li>
              <li>{formData.special_condition_2 || 'Additional conditions, if any, may be appended as a separate schedule.'}</li>
            </ol>
          </div>
        </div>

        {/* Signatures - Professional Design */}
        <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '3px solid #F97316' }}>
          <h2 style={{
            fontSize: '13px',
            fontWeight: 'bold',
            marginBottom: '20px',
            color: '#2c3e50',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            4. AUTHORIZED SIGNATURES
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
            {/* Lessor Signature Block */}
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontSize: '8px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                  Live Photo (identity verification)
                </div>
                <div data-photobox="lessor" style={{
                  height: '90px',
                  width: '100%',
                  border: '2px dashed #bdc3c7',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'
                }}>
                  {photoImage && (
                    <img src={photoImage} alt="Lessor Photo" style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
                  )}
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontSize: '8px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                  Signature
                </div>
                <div data-sigbox="lessor" style={{
                  height: '70px',
                  width: '70px',
                  margin: '0 auto',
                  border: '2px dashed #bdc3c7',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'
                }}>
                  {signatureImage && (
                    <img src={signatureImage} alt="Lessor Signature" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                  )}
                </div>
              </div>
              <div style={{ borderTop: '2px solid #2c3e50', paddingTop: '10px', marginTop: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: '600', color: '#2c3e50', marginBottom: '3px' }}>
                  {isCommercial ? formData.lessor_full_name : formData.licensor_full_name || '_____________________'}
                </div>
                <div style={{ fontSize: '9px', color: '#7f8c8d' }}>Property Owner / Lessor</div>
                <div style={{ fontSize: '8px', color: '#95a5a6', marginTop: '5px' }}>
                  {new Date().toLocaleDateString('en-IN')}
                </div>
              </div>
            </div>

            {/* Lessee Signature Block */}
            <div style={{
              border: '1px solid #ecf0f1',
              borderRadius: '6px',
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontSize: '8px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                  Live Photo (identity verification)
                </div>
                <div data-photobox="lessee" style={{
                  height: '90px',
                  width: '100%',
                  border: '2px dashed #bdc3c7',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'
                }}>
                  {photoImage && (
                    <img src={photoImage} alt="Lessee Photo" style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
                  )}
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontSize: '8px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                  Signature
                </div>
                <div data-sigbox="lessee" style={{
                  height: '70px',
                  width: '70px',
                  margin: '0 auto',
                  border: '2px dashed #bdc3c7',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'
                }}>
                  {signatureImage && (
                    <img src={signatureImage} alt="Lessee Signature" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                  )}
                </div>
              </div>
              <div style={{ borderTop: '2px solid #2c3e50', paddingTop: '10px', marginTop: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: '600', color: '#2c3e50', marginBottom: '3px' }}>
                  {isCommercial ? formData.lessee_full_name : formData.licensee_full_name || '_____________________'}
                </div>
                <div style={{ fontSize: '9px', color: '#7f8c8d' }}>Tenant / Lessee</div>
                <div style={{ fontSize: '8px', color: '#95a5a6', marginTop: '5px' }}>
                  {new Date().toLocaleDateString('en-IN')}
                </div>
              </div>
            </div>
          </div>

          {/* Witness Signatures - optional. If a witness's email was provided, they get a
              no-account link to preview the agreement and sign; their signature (and
              optional photo) is stamped into these boxes the same way as the parties'. */}
          {formData.witness1_name || formData.witness2_name ? (
            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ecf0f1' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#2c3e50', marginBottom: '15px', textTransform: 'uppercase' }}>Witness</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '8px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                      Live Photo (optional)
                    </div>
                    <div data-photobox="witness1" style={{
                      height: '90px',
                      width: '100%',
                      border: '2px dashed #bdc3c7',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#ffffff',
                      overflow: 'hidden'
                    }}>
                      {photoImage && (
                        <img src={photoImage} alt="Witness 1 Photo" style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '8px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                      Signature
                    </div>
                    <div data-sigbox="witness1" style={{
                      height: '70px',
                      width: '70px',
                      margin: '0 auto',
                      border: '2px dashed #bdc3c7',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#ffffff',
                      overflow: 'hidden'
                    }}>
                      {signatureImage && (
                        <img src={signatureImage} alt="Witness 1 Signature" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: '9px', fontWeight: '600', color: '#2c3e50' }}>
                    {formData.witness1_name || '_____________________'}
                  </div>
                  <div style={{ fontSize: '8px', color: '#7f8c8d' }}>Witness 1</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '8px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                      Live Photo (optional)
                    </div>
                    <div data-photobox="witness2" style={{
                      height: '90px',
                      width: '100%',
                      border: '2px dashed #bdc3c7',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#ffffff',
                      overflow: 'hidden'
                    }}>
                      {photoImage && (
                        <img src={photoImage} alt="Witness 2 Photo" style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '8px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                      Signature
                    </div>
                    <div data-sigbox="witness2" style={{
                      height: '70px',
                      width: '70px',
                      margin: '0 auto',
                      border: '2px dashed #bdc3c7',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#ffffff',
                      overflow: 'hidden'
                    }}>
                      {signatureImage && (
                        <img src={signatureImage} alt="Witness 2 Signature" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: '9px', fontWeight: '600', color: '#2c3e50' }}>
                    {formData.witness2_name || '_____________________'}
                  </div>
                  <div style={{ fontSize: '8px', color: '#7f8c8d' }}>Witness 2</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Professional Footer */}
        <div style={{
          marginTop: '50px',
          paddingTop: '20px',
          paddingBottom: '15px',
          borderTop: '2px solid #2c3e50',
          textAlign: 'center',
          fontSize: '9px',
          color: '#7f8c8d',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          marginLeft: '-50px',
          marginRight: '-50px',
          marginBottom: '-50px',
          padding: '20px 50px'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#2c3e50' }}>PROPERTY LEASE AGREEMENT</strong> - Comprehensive Agreement
          </div>
          <div style={{ marginBottom: '8px', fontSize: '8px', color: '#95a5a6' }}>
            This is a digitally generated comprehensive lease agreement. All parties acknowledge their acceptance through digital signatures.
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '15px',
            fontSize: '8px',
            marginTop: '10px',
            paddingTop: '10px',
            borderTop: '1px solid #ecf0f1'
          }}>
            <div>
              <div style={{ color: '#7f8c8d', fontWeight: '600' }}>Document ID</div>
              <div style={{ color: '#2c3e50', fontSize: '9px', marginTop: '2px' }}>{agreementId || 'AGR-' + new Date().getTime().toString().slice(-8)}</div>
            </div>
            <div>
              <div style={{ color: '#7f8c8d', fontWeight: '600' }}>Generated Date</div>
              <div style={{ color: '#2c3e50', fontSize: '9px', marginTop: '2px' }}>{new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
            <div>
              <div style={{ color: '#7f8c8d', fontWeight: '600' }}>Version</div>
              <div style={{ color: '#2c3e50', fontSize: '9px', marginTop: '2px' }}>2.0 - Comprehensive Edition</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Generate PDF blob from preview (returns blob for saving/sharing)
  // Uses the SAME logic as downloadPDF to ensure consistency
  const generatePDFBlob = async () => {
    try {
      const element = agreementPreviewRef.current;

      // Use the EXACT same html2canvas settings as downloadPDF
      // This ensures we capture the same 12-page PDF shown to the user
      const canvas = await html2canvas(element, {
        scale: 2,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Use JPEG (quality 0.8) instead of PNG. The page is text on a solid white
      // background, so JPEG compresses it from ~40MB (lossless PNG) down to ~2-3MB
      // with no visible quality loss. This keeps the PDF small enough to store and
      // fast to preview/download.
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Measure the signature AND photo boxes so the backend can stamp each party's
      // signature/photo into them at sign time. The captured element maps its full
      // width to pageWidth (A4 = 210mm), so a single mm-per-pixel factor converts any
      // DOM offset into PDF coordinates.
      try {
        const elementRect = element.getBoundingClientRect();
        const mmPerPx = pageWidth / elementRect.width;
        const boxes = [];
        const measureBox = (boxEl, role, kind) => {
          const r = boxEl.getBoundingClientRect();
          const yTotalMm = (r.top - elementRect.top) * mmPerPx;
          const page = Math.floor(yTotalMm / pageHeight); // 0-indexed
          boxes.push({
            role,   // 'lessor' | 'lessee'
            kind,   // 'signature' | 'photo'
            page,
            x_mm: (r.left - elementRect.left) * mmPerPx,
            y_mm: yTotalMm - page * pageHeight,
            w_mm: r.width * mmPerPx,
            h_mm: r.height * mmPerPx,
          });
        };
        element.querySelectorAll('[data-sigbox]').forEach((boxEl) => {
          measureBox(boxEl, boxEl.getAttribute('data-sigbox'), 'signature');
        });
        element.querySelectorAll('[data-photobox]').forEach((boxEl) => {
          measureBox(boxEl, boxEl.getAttribute('data-photobox'), 'photo');
        });
        signatureBoxesRef.current = boxes;
        console.log('📐 Measured signature/photo boxes:', boxes);
      } catch (measureErr) {
        console.warn('⚠️ Could not measure signature/photo boxes (stamping will be skipped):', measureErr);
        signatureBoxesRef.current = [];
      }

      const outBlob = pdf.output('blob');
      console.log(`✅ PDF blob generated from preview (${(outBlob.size / 1024 / 1024).toFixed(2)} MB)`);
      return outBlob;
    } catch (error) {
      console.error('PDF generation error:', error);
      throw error;
    }
  };

  // Expose generatePDFBlob to parent component via ref
  useImperativeHandle(ref, () => ({
    generatePDFBlob,
    // Returns the signature box coordinates measured during the last generatePDFBlob()
    getSignatureBoxes: () => signatureBoxesRef.current
  }));

  // Download PDF
  const downloadPDF = async () => {
    setIsGenerating(true);
    try {
      const pdfBlob = await generatePDFBlob();
      const fileName = `agreement_${formData.property_state || 'agreement'}_${new Date().getTime()}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download error:', error);
      alert('Error downloading PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Preview-only mode (for Step 8): Show simple preview without signature interface
  if (previewOnly) {
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        color: '#f5f5f5',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #404040'
      }}>
        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #404040',
          borderRadius: '8px',
          overflow: 'auto',
          padding: '20px',
          maxHeight: '600px',
          scrollBehavior: 'smooth'
        }}>
          <div ref={agreementPreviewRef}>
            {generateAgreementContent({ includeSignature: false })}
          </div>
        </div>
        <button
          onClick={downloadPDF}
          disabled={isGenerating}
          style={{
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 20px',
            backgroundColor: '#F97316',
            color: '#1a1a1a',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            opacity: isGenerating ? 0.6 : 1,
            transition: 'all 0.2s ease',
            fontSize: '14px'
          }}
        >
          <Download size={18} />
          {isGenerating ? 'Generating PDF...' : 'Download PDF'}
        </button>
      </div>
    );
  }

  // Full mode: Show grid with preview + signature interface
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '30px',
      backgroundColor: '#1a1a1a',
      color: '#f5f5f5',
      padding: '30px',
      borderRadius: '12px',
      minHeight: '100vh'
    }}>
      {/* LEFT SIDE - AGREEMENT PREVIEW */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '10px'
        }}>
          <FileText size={20} color="#F97316" />
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Agreement Preview</h2>
        </div>

        <div style={{
          flex: 1,
          backgroundColor: '#2a2a2a',
          border: '1px solid #404040',
          borderRadius: '8px',
          overflow: 'auto',
          padding: '20px',
          maxHeight: 'calc(100vh - 200px)',
          scrollBehavior: 'smooth'
        }}>
          <div ref={agreementPreviewRef}>
            {generateAgreementContent({ includeSignature: signatureImage !== null })}
          </div>
        </div>

        <button
          onClick={downloadPDF}
          disabled={isGenerating}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            backgroundColor: '#F97316',
            color: '#1a1a1a',
            padding: '12px 20px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            opacity: isGenerating ? 0.6 : 1,
            transition: 'all 0.2s ease'
          }}
        >
          <Download size={18} />
          {isGenerating ? 'Generating PDF...' : 'Download PDF'}
        </button>
      </div>

      {/* RIGHT SIDE - SIGNATURE CAPTURE */}
      {/* PRODUCTION RULE: Only show signature capture to signatories (tenant/landlord) */}
      {/* Hide from: Brokers, Agents, Self-managed landlords (they don't sign) */}
      {(userRole === 'tenant' || userRole === 'landlord') && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '10px'
        }}>
          <Eye size={20} color="#F97316" />
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Add Signature</h2>
        </div>

        {/* PAYMENT BREAKDOWN SECTION */}
        {/* PRODUCTION RULE: Only show Payment Summary to users who are actually paying */}
        {/* Hide from: Brokers, Agents, Self-managed landlords (they don't pay) */}
        {/* Show to: Tenants (always pay), Regular landlords (if selected to pay fee) */}
        {(userRole === 'tenant' || userRole === 'landlord') && (
          <div style={{
            backgroundColor: '#2a2a2a',
            border: '2px solid #F97316',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '10px'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#F97316',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              💰 Payment Summary
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {getPaymentDisplayItems().map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #404040',
                    borderRadius: '6px',
                    padding: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: item.isEarning ? '#10b981' : '#f5f5f5',
                      marginBottom: '4px'
                    }}>
                      {item.isEarning ? '✅ ' : '💳 '}{item.label}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#808080',
                      marginBottom: '4px'
                    }}>
                      {item.description}
                    </div>
                    <div style={{
                      fontSize: '10px',
                      color: '#606060',
                      backgroundColor: '#2a2a2a',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      display: 'inline-block'
                    }}>
                      → {item.account}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: item.isEarning ? '#10b981' : '#F97316',
                    minWidth: '80px',
                    textAlign: 'right'
                  }}>
                    {item.isEarning ? '+' : ''}₹{(item.amount / 100).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            {/* Total Amount */}
            <div style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid #404040',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#a3a3a3' }}>
                Total to Pay:
              </div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#F97316' }}>
                ₹{(getPaymentDisplayItems().reduce((sum, item) => sum + (item.isEarning ? 0 : item.amount), 0) / 100).toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {/* Mode Selection Tabs */}
        <div style={{
          display: 'flex',
          gap: '10px',
          borderBottom: '1px solid #404040',
          paddingBottom: '10px'
        }}>
          {['draw', 'upload'].map(mode => (
            <button
              key={mode}
              onClick={() => {
                setSignatureMode(mode);
                setSignatureImage(null);
                if (mode === 'draw') clearSignature();
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: signatureMode === mode ? '#F97316' : 'transparent',
                color: signatureMode === mode ? '#1a1a1a' : '#a3a3a3',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '13px',
                transition: 'all 0.2s ease'
              }}
            >
              {mode === 'draw' ? '✏️ Draw' : '📤 Upload'}
            </button>
          ))}
        </div>

        {/* Draw Signature */}
        {signatureMode === 'draw' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ fontSize: '12px', color: '#a3a3a3' }}>
              Draw your signature below:
            </div>
            <canvas
              ref={canvasRef}
              style={{
                flex: 1,
                border: '2px solid #404040',
                borderRadius: '8px',
                cursor: 'crosshair',
                backgroundColor: '#ffffff',
                minHeight: '200px',
                maxHeight: '400px'
              }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={captureSignature}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#10b981',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.2s ease'
                }}
              >
                ✓ Capture Signature
              </button>
              <button
                onClick={clearSignature}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#ef4444',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.2s ease'
                }}
              >
                ✕ Clear
              </button>
            </div>
          </div>
        )}

        {/* Upload Signature */}
        {signatureMode === 'upload' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ fontSize: '12px', color: '#a3a3a3' }}>
              Upload a signature image (PNG, JPG, GIF):
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '30px 20px',
                backgroundColor: '#404040',
                color: '#a3a3a3',
                border: '2px dashed #666666',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s ease',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#505050';
                e.target.style.borderColor = '#888888';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#404040';
                e.target.style.borderColor = '#666666';
              }}
            >
              Click to upload signature image
            </button>
            {signatureImage && (
              <button
                onClick={() => {
                  setSignatureImage(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                style={{
                  padding: '10px',
                  backgroundColor: '#ef4444',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                ✕ Remove Signature
              </button>
            )}
          </div>
        )}

        {/* Signature Preview */}
        {signatureImage && (
          <div style={{
            backgroundColor: '#2a2a2a',
            border: '1px solid #404040',
            borderRadius: '8px',
            padding: '15px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#a3a3a3', marginBottom: '10px' }}>
              Signature Preview:
            </div>
            <img
              src={signatureImage}
              alt="Signature Preview"
              style={{
                maxHeight: '120px',
                maxWidth: '100%',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                padding: '10px'
              }}
            />
          </div>
        )}

        {/* Signer Name */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '12px', color: '#a3a3a3' }}>
            Signature Name (typed):
          </label>
          <input
            type="text"
            value={signatureName}
            onChange={(e) => setSignatureName(e.target.value)}
            placeholder="Your name"
            style={{
              padding: '10px',
              backgroundColor: '#2a2a2a',
              border: '1px solid #404040',
              borderRadius: '6px',
              color: '#f5f5f5',
              fontSize: '13px',
              outline: 'none'
            }}
          />
        </div>

        {/* Status */}
        {signatureImage && signatureName && (
          <div style={{
            padding: '12px',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '6px',
            color: '#10b981',
            fontSize: '12px',
            textAlign: 'center',
            fontWeight: 500
          }}>
            ✓ Signature ready for signing
          </div>
        )}
      </div>
      )}
    </div>
  );
});

AgreementPDFPreview.displayName = 'AgreementPDFPreview';

export default AgreementPDFPreview;
